<div class="blog-form-dialog">

    <!-- Header -->
    <header class="dialog-header">
        <div class="header-icon">
            <mat-icon>{{ isEditMode ? 'edit' : 'post_add' }}</mat-icon>
        </div>

        <div class="header-title">
            <h2>{{ isEditMode ? 'Edit Blog' : 'Create Blog' }}</h2>
            <p>
                {{
                isEditMode
                ? 'Update blog content and SEO settings.'
                : 'Add a new blog post to your website.'
                }}
            </p>
        </div>

        <button mat-icon-button type="button" aria-label="Close dialog" [disabled]="saving" (click)="cancel()">
            <mat-icon>close</mat-icon>
        </button>
    </header>

    <mat-divider />

    <form [formGroup]="form" (ngSubmit)="saveBlog()">

        <mat-dialog-content class="form-content">

            <!-- Basic information -->
            <section class="form-section">
                <div class="section-heading">
                    <mat-icon>article</mat-icon>
                    <div>
                        <h3>Basic Information</h3>
                        <p>Enter the main details of your blog post.</p>
                    </div>
                </div>

                <div class="form-grid">

                    <mat-form-field appearance="outline" class="full-width">
                        <mat-label>Blog Title</mat-label>
                        <input matInput formControlName="title" maxlength="255" placeholder="Enter blog title" />

                        <mat-hint align="end">
                            {{ title.value?.length || 0 }}/255
                        </mat-hint>

                        <mat-error *ngIf="title.hasError('required')">
                            Blog title is required.
                        </mat-error>

                        <mat-error *ngIf="title.hasError('maxlength')">
                            Maximum 255 characters allowed.
                        </mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="full-width">
                        <mat-label>Short Description (HTML supported)</mat-label>
                        <textarea matInput formControlName="short_description" rows="4"
                            placeholder="Enter a short summary of the blog"></textarea>

                        <mat-error *ngIf="shortDescription.hasError('required')">
                            Short description is required.
                        </mat-error>
                    </mat-form-field>

                    <!-- Full Blog Content Rich Text Editor -->
                    <div class="editor-field-wrapper full-width">
                        <label class="editor-label">Full Blog Content *</label>

                        <quill-editor formControlName="description"
                            placeholder="Enter or paste the complete blog content..." [modules]="quillConfig"
                            format="html" (onEditorCreated)="onEditorCreated($event)" class="custom-quill-editor">
                        </quill-editor>

                        <div class="editor-hint">
                            Rich text toolbar enables formatting for headings, bullet points, code blocks, and links.
                        </div>

                        <mat-error *ngIf="description.touched && description.hasError('required')" class="editor-error">
                            Blog content is required.
                        </mat-error>
                    </div>

                </div>
            </section>

            <mat-divider />

            <!-- Featured image -->
            <section class="form-section">
                <div class="section-heading">
                    <mat-icon>image</mat-icon>
                    <div>
                        <h3>Featured Image</h3>
                        <p>Upload a JPG, PNG or WebP image, up to 5 MB.</p>
                    </div>
                </div>

                <div class="image-upload-area">

                    <div class="image-preview" *ngIf="imagePreview">
                        <img [src]="imagePreview" alt="Blog featured image preview" />

                        <button mat-icon-button type="button" class="remove-image" aria-label="Remove featured image"
                            [disabled]="saving" (click)="removeSelectedImage()">
                            <mat-icon>close</mat-icon>
                        </button>
                    </div>

                    <div class="upload-placeholder" *ngIf="!imagePreview">
                        <mat-icon>cloud_upload</mat-icon>
                        <strong>No featured image selected</strong>
                        <span>Choose an image from your device.</span>
                    </div>

                    <label class="upload-button">
                        <mat-icon>upload_file</mat-icon>
                        {{ imagePreview ? 'Replace Image' : 'Choose Image' }}

                        <input #fileInput type="file" accept="image/*" [disabled]="saving || uploadingImage"
                            (change)="previewpImage(fileInput.files)" />
                    </label>

                    <div *ngIf="uploadingImage" class="uploading-spinner">
                        <mat-spinner diameter="20"></mat-spinner>
                        <span>Uploading image...</span>
                    </div>

                </div>
            </section>

            <mat-divider />

            <!-- Publishing -->
            <section class="form-section">
                <div class="section-heading">
                    <mat-icon>publish</mat-icon>
                    <div>
                        <h3>Publishing Details</h3>
                        <p>Set the author, publication status and visibility.</p>
                    </div>
                </div>

                <div class="form-grid two-columns">

                    <mat-form-field appearance="outline">
                        <mat-label>Author</mat-label>
                        <input matInput formControlName="author" placeholder="Enter author name" />

                        <mat-error>Author is required.</mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="outline">
                        <mat-label>Publication Status</mat-label>

                        <mat-select formControlName="status">
                            <mat-option value="Draft">Draft</mat-option>
                            <mat-option value="Published">Published</mat-option>
                            <mat-option value="Pending">Pending</mat-option>
                        </mat-select>
                    </mat-form-field>

                    <mat-form-field appearance="outline">
                        <mat-label>Visibility</mat-label>

                        <mat-select formControlName="is_active">
                            <mat-option value="1">Active</mat-option>
                            <mat-option value="0">Inactive</mat-option>
                        </mat-select>
                    </mat-form-field>

                </div>
            </section>

            <mat-divider />

            <!-- SEO -->
            <section class="form-section">
                <div class="section-heading">
                    <mat-icon>manage_search</mat-icon>
                    <div>
                        <h3>SEO Settings</h3>
                        <p>Configure search engine metadata for this blog.</p>
                    </div>
                </div>

                <div class="form-grid">

                    <mat-form-field appearance="outline" class="full-width">
                        <mat-label>Meta Title</mat-label>
                        <input matInput formControlName="meta_title" placeholder="Enter SEO title" />
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="full-width">
                        <mat-label>H1 Tag</mat-label>
                        <input matInput formControlName="h1_tag" placeholder="Enter the main page heading" />
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="full-width">
                        <mat-label>Meta Keywords</mat-label>
                        <textarea matInput formControlName="meta_keywords" rows="2"
                            placeholder="Enter comma-separated keywords"></textarea>
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="full-width">
                        <mat-label>Meta Description</mat-label>
                        <textarea matInput formControlName="meta_description" rows="3"
                            placeholder="Enter SEO meta description"></textarea>
                    </mat-form-field>

                </div>
            </section>

            <!-- Error message -->
            <div class="error-message" *ngIf="errorMessage" role="alert">
                <mat-icon>error_outline</mat-icon>
                <span>{{ errorMessage }}</span>
            </div>

        </mat-dialog-content>

        <mat-divider />

        <!-- Footer -->
        <footer class="dialog-footer">
            <button mat-stroked-button type="button" [disabled]="saving" (click)="cancel()">
                Cancel
            </button>

            <button mat-flat-button color="primary" type="submit" [disabled]="saving">
                <mat-spinner *ngIf="saving" diameter="18"></mat-spinner>

                <mat-icon *ngIf="!saving">
                    {{ isEditMode ? 'save' : 'add' }}
                </mat-icon>

                {{
                saving
                ? 'Saving...'
                : isEditMode
                ? 'Save Changes'
                : 'Create Blog'
                }}
            </button>
        </footer>

    </form>
</div>